import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { loadSamplePortfolio, SAMPLE_PHASE_KEY, START_SAMPLE_KEY } from "@/lib/sampleData";
import { SampleLoader } from "@/components/sample/SampleLoader";
import { MoneyStuckReveal } from "@/components/sample/MoneyStuckReveal";
import { SampleTour } from "@/components/sample/SampleTour";

// Orchestrates the premium first-run sample experience:
// cinematic loader → "money stuck" reveal → (reload to populate) → guided tour.
// Mounted once in Layout so it survives the single dashboard reload via sessionStorage.
export function SamplePortfolioExperience() {
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState("idle"); // idle | loading | reveal | tour
  const [reveal, setReveal] = useState({ amount: 0, settlementCount: 0, followupCount: 0 });
  const loaderDone = useRef(false);
  const dataReady = useRef(false);

  const setPhaseFlag = (p) => {
    if (p) sessionStorage.setItem(SAMPLE_PHASE_KEY, p);
    else sessionStorage.removeItem(SAMPLE_PHASE_KEY);
    window.dispatchEvent(new Event("recoveros:sample-phase"));
  };

  // Resume the tour after the dashboard reload.
  useEffect(() => {
    if (sessionStorage.getItem(SAMPLE_PHASE_KEY) === "tour") {
      if (location.pathname !== "/dashboard") { navigate("/dashboard"); return; }
      setPhase("tour");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const beginReveal = useCallback(async () => {
    let d = {};
    try { d = (await api.get("/dashboard/money-stuck")).data || {}; } catch (e) { /* fallback below */ }
    setReveal({
      amount: d.potential_settlement_value || d.total_outstanding || 0,
      settlementCount: d.settlement_candidates || 0,
      followupCount: d.accounts_needing_followup || 0,
    });
    dataReady.current = true;
    if (loaderDone.current) setPhase("reveal");
  }, []);

  const start = useCallback(async () => {
    if (sessionStorage.getItem(SAMPLE_PHASE_KEY)) return; // already running
    loaderDone.current = false; dataReady.current = false;
    setPhaseFlag("loading");
    setPhase("loading");
    try {
      await loadSamplePortfolio();
      await beginReveal();
    } catch (e) {
      setPhaseFlag(null);
      setPhase("idle");
      toast.error(e?.response?.data?.detail || "Could not load the sample portfolio");
    }
  }, [beginReveal]);

  useEffect(() => {
    const onStart = () => start();
    window.addEventListener("recoveros:start-sample", onStart);
    return () => window.removeEventListener("recoveros:start-sample", onStart);
  }, [start]);

  // One-shot launch: from outside Layout via sessionStorage flag (onboarding), OR from an
  // email deep-link `/dashboard?start=sample`. The URL param is stripped after firing so a
  // refresh won't relaunch it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get("start") === "sample";
    if (!fromUrl && !sessionStorage.getItem(START_SAMPLE_KEY)) return;
    if (location.pathname !== "/dashboard") {
      if (fromUrl) sessionStorage.setItem(START_SAMPLE_KEY, "1");
      navigate("/dashboard");
      return;
    }
    sessionStorage.removeItem(START_SAMPLE_KEY);
    if (fromUrl) {
      params.delete("start");
      const qs = params.toString();
      window.history.replaceState({}, "", `/dashboard${qs ? `?${qs}` : ""}`);
    }
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  const onLoaderDone = useCallback(() => {
    loaderDone.current = true;
    if (dataReady.current) setPhase("reveal");
  }, []);

  const onEnterDashboard = useCallback(() => {
    setPhaseFlag("tour");
    window.location.assign("/dashboard"); // hard reload → dashboard repopulates cleanly
  }, []);

  const onTourDone = useCallback(() => {
    setPhaseFlag(null);
    setPhase("idle");
    window.dispatchEvent(new Event("recoveros:sample-done"));
  }, []);

  return (
    <AnimatePresence>
      {phase === "loading" && <SampleLoader key="loader" onDone={onLoaderDone} />}
      {phase === "reveal" && (
        <MoneyStuckReveal key="reveal" amount={reveal.amount} settlementCount={reveal.settlementCount}
          followupCount={reveal.followupCount} onEnter={onEnterDashboard} />
      )}
      {phase === "tour" && <SampleTour key="tour" onDone={onTourDone} />}
    </AnimatePresence>
  );
}
